import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { ToggleButton } from "primereact/togglebutton";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

const apiUrl = process.env.REACT_APP_API_URL;

export default function MailConfig() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [showPassword, setShowPassword] = useState({});
  const [editingConfig, setEditingConfig] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // État pour l'édition
  const [editData, setEditData] = useState({
    id: '',
    compte: '',
    cle: '',
    valeur: ''
  });

  const compteOptions = [
    { label: 'ZotDevis', value: 'zotdevis' },
    { label: 'Otedalon', value: 'otedalon' },
  ];

  const cleOptions = [
    { label: 'Username', value: 'username' },
    { label: 'Password', value: 'password' },
    { label: 'Host', value: 'host' },
    { label: 'Port', value: 'port' },
  ];

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = () => {
    setLoading(true);
    axios
      .get(`${apiUrl}/IHM/api/mails/mails_config.php`)
      .then((response) => {
        if (response.data.success) {
          setConfigs(response.data.data);
        } else {
          showToast("Erreur: " + response.data.message, "error");
        }
      })
      .catch((error) => {
        console.error("Erreur:", error);
        showToast("Erreur lors du chargement", "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleEdit = (rowData) => {
    setEditingConfig(rowData);
    setEditData({
      id: rowData.id,
      compte: rowData.compte || '',
      cle: rowData.cle || '',
      valeur: rowData.valeur || ''
    });
    setEditDialogVisible(true);
  };

  const handleSave = async () => {
    try {
      const response = await axios.put(
        `${apiUrl}/IHM/api/mails/mails_config.php`,
        {
          id: editData.id,
          valeur: editData.valeur
        }
      );

      if (response.data.success) {
        fetchConfigs();
        setEditDialogVisible(false);
        showToast("Configuration mise à jour avec succès !", "success");
      } else {
        showToast("Erreur: " + response.data.message, "error");
      }
    } catch (error) {
      console.error("Erreur:", error);
      showToast("Erreur lors de l'enregistrement", "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette configuration ?")) {
      return;
    }

    try {
      const response = await axios.delete(
        `${apiUrl}/IHM/api/mails/mails_config.php`,
        { data: { id: id } }
      );

      if (response.data.success) {
        fetchConfigs();
        showToast("Configuration supprimée avec succès", "success");
      } else {
        showToast("Erreur: " + response.data.message, "error");
      }
    } catch (error) {
      console.error("Erreur:", error);
      showToast("Erreur lors de la suppression", "error");
    }
  };

  const toggleShowPassword = (id) => {
    setShowPassword(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const showToast = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const valueBodyTemplate = (rowData) => {
    const isPassword = rowData.cle === 'password';
    const isVisible = showPassword[rowData.id] || false;
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontFamily: isPassword ? 'monospace' : 'inherit' }}>
          { rowData.valeur}
        </span>
      </div>
    );
  };

  // Template pour les actions
  const actionBodyTemplate = (rowData) => {
    return (
      <div style={{ display: "flex", justifyContent: "center", gap: "5px" }}>
        <Button
          icon="pi pi-pencil"
          severity="info"
          text
          raised
          size="small"
          tooltip="Modifier"
          tooltipOptions={{ position: "top", style: { fontSize: "0.7rem" } }}
          onClick={() => handleEdit(rowData)}
        />
        
      </div>
    );
  };

  const compteBodyTemplate = (rowData) => {
    const colors = {
      zotdevis: '#3b82f6',
      otedalon: '#22c55e'
    };
    return (
      <span style={{
        backgroundColor: colors[rowData.compte] || '#6b7280',
        color: 'white',
        padding: '2px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 500
      }}>
        {rowData.compte}
      </span>
    );
  };




  const keyBodyTemplate = (rowData) => {
    if (rowData.cle === "username") {
      return <span>Utilisateur</span>;
    } else {
     return <span>Clé</span>;
    }
  };

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        action={
          <IconButton size="small" color="inherit" onClick={handleCloseSnackbar}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: snackbarSeverity === 'error' ? '#ef4444' : '#22c55e',
          }
        }}
      />

      <DataTable
        value={configs}
        paginator
        rows={25}
        size="small"
        tableStyle={{ minWidth: "50rem" }}
        scrollable
        scrollDirection="horizontal"
        paginatorTemplate="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink RowsPerPageDropdown"
        paginatorRight={
          <span style={{ margin: "10px", fontWeight: "bold" }}>
            Résultats : {configs.length}
          </span>
        }
        loading={loading}
        loadingIcon="pi pi-spin pi-spinner"
        emptyMessage={
          loading
            ? "Chargement des configurations..."
            : "Aucune configuration"
        }
      >
        <Column field="id" header="ID" style={{ width: "80px" }} />
        <Column 
          field="compte" 
          header="Compte" 
          style={{ width: "150px" }}
          body={compteBodyTemplate}
        />
        <Column 
          field="cle" 
          header="Clé" 
          style={{ width: "150px" }}
          body={keyBodyTemplate}
        />
        <Column 
          field="valeur" 
          header="Valeur" 
          style={{ minWidth: "300px" }}
          body={valueBodyTemplate}
        />
        <Column
          header="Actions"
          style={{ textAlign: "center", width: "120px" }}
          body={actionBodyTemplate}
        />
      </DataTable>

    
      <Dialog
        header="Modifier la configuration"
        visible={editDialogVisible}
        style={{ width: "600px", maxWidth: "90vw" }}
        modal
        onHide={() => setEditDialogVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            
            <Button
              label="Enregistrer"
              icon="pi pi-check"
              onClick={handleSave}
              style={{
                background: "#0971b7",
                border: "none",
                color: "white",
              }}
              className="w-full"
              autoFocus
            />
          </div>
        }
      >
        <div style={{ padding: "20px" }}>
          
         
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "bold",
                fontSize: "14px",
                color: "#374151"
              }}
            >
              Valeur
            </label>
            {editData.cle === 'password' ? (
              <InputText
                type="text"
                value={editData.valeur}
                onChange={(e) => setEditData({ ...editData, valeur: e.target.value })}
                style={{ width: "100%" }}
                placeholder="Entrez la nouvelle valeur"
              />
            ) : (
              <InputText
                value={editData.valeur}
                onChange={(e) => setEditData({ ...editData, valeur: e.target.value })}
                style={{ width: "100%" }}
                placeholder="Entrez la nouvelle valeur"
              />
            )}
            {editData.cle === 'password' && (
              <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                 Attention: La modification du mot de passe peut affecter l'envoi des emails
              </small>
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
}